import { InteractiveCTA, VideoChapterItem } from 'types';
import { PlayerDesign } from 'queries/details/usePlaybackDetailsQuery';

// Mirrors express-api's ShowroomManifestResponse. The page renders from this
// object alone and never reads other /details fields.

export const SHOWROOM_TEMPLATE_ID = -3;

export enum ShowroomStage {
  FIRST_RESPONSE = 'first_response',
  APPT_REMINDER = 'appt_reminder',
  APPT_FOLLOWUP = 'appt_followup',
  POST_SALE = 'post_sale',
  NO_VEHICLE = 'no_vehicle',
}

export enum ShowroomModuleKey {
  VIDEO = 'video',
  IDENTITY = 'identity',
  GALLERY = 'gallery',
  VEHICLE_DETAIL = 'vehicleDetail',
}

export enum ShowroomDockAction {
  SCHEDULE = 'schedule',
  DIRECTIONS = 'directions',
  TALK_NUMBERS = 'talkNumbers',
  BOOK_SERVICE = 'bookService',
  BOOK_CALL = 'bookCall',
  ASK = 'ask',
}

export interface ShowroomTheme {
  primary: string;
  headerBackground: string;
  headerText: string;
  logo: string | null;
}

export interface ShowroomCopyTokens {
  firstName: string;
  repName: string;
  repFirstName: string;
  year: string;
  make: string;
  model: string;
  trim: string;
}

export interface ShowroomRep {
  id: number;
  firstName: string;
  lastName: string;
  title: string;
  profilePictureUrl: string | null;
  phone: string;
  email: string;
}

export interface SalesHoursTime {
  hours: string;
  meridiem: 'AM' | 'PM';
}

export interface SalesHoursRange {
  from: SalesHoursTime;
  to: SalesHoursTime;
}

export const SALES_HOURS_DAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export type SalesHoursDay = (typeof SALES_HOURS_DAYS)[number];

export type SalesHours = Partial<
  Record<SalesHoursDay, SalesHoursRange | SalesHoursRange[] | null>
> & { timezone?: string };

export interface ShowroomDealer {
  name: string;
  logo: string | null;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  website: string;
  salesHours: SalesHours | null;
  mapEmbedHtml: string;
}

export interface ShowroomDock {
  primary: ShowroomDockAction;
  secondary: ShowroomDockAction;
}

export interface ShowroomShell {
  rep: ShowroomRep;
  dealer: ShowroomDealer;
  dock: ShowroomDock;
  disclaimer: string | null;
  appointment: null;
  askAi: null;
}

export interface ShowroomVideoModuleData {
  id: number;
  title: string;
  thumbnail: string;
  videoSource: string;
  videoLength: number;
  chapters: VideoChapterItem[] | null;
  protected: number;
  playerDesign: PlayerDesign | null;
  interactiveCTAs: InteractiveCTA[];
}

export interface ShowroomIdentityModuleData {
  condition: string;
  certified: boolean | null;
  year: string;
  make: string;
  model: string;
  trim: string;
  vin: string;
  stockNumber: string;
  sold: boolean;
}

export interface ShowroomGalleryImage {
  id: string;
  url: string;
  thumbnail: string;
}

export interface ShowroomGalleryModuleData {
  images: ShowroomGalleryImage[];
}

export interface ShowroomVehicleDetailModuleData {
  exteriorColor: string;
  interiorColor: string;
  drivetrain: string;
  mileage: number | null;
  engine: string;
  transmission: string;
  cityMpg: number | null;
  highwayMpg: number | null;
}

export type ShowroomModule =
  | { key: ShowroomModuleKey.VIDEO; data: ShowroomVideoModuleData }
  | { key: ShowroomModuleKey.IDENTITY; data: ShowroomIdentityModuleData }
  | { key: ShowroomModuleKey.GALLERY; data: ShowroomGalleryModuleData }
  | {
      key: ShowroomModuleKey.VEHICLE_DETAIL;
      data: ShowroomVehicleDetailModuleData;
    };

export interface ShowroomMeta {
  title: string;
  description: string;
}

export interface ShowroomManifest {
  version: number;
  stage: ShowroomStage;
  language: 'en' | 'es' | 'fr';
  theme: ShowroomTheme;
  tokens: ShowroomCopyTokens;
  shell: ShowroomShell;
  modules: ShowroomModule[];
  meta: ShowroomMeta;
}
